import {
  App,
  composeInitialState,
  LoginPlugin,
  LoginPluginPartialAppState,
  UniversalAppState,
} from "@pimo/pimo-app-builder";
import {
  DefaultOverlayPartialAppState,
  initialDefaultOverlayState,
} from "@pimo/pimo-components";
import { L10nPlugin, SupportedLanguage } from "@pimo/pimo-l10n";

import { APP_KEY } from "../app-key";
import {
  SnackbarOverlay,
  SnackbarOverlayProps,
} from "./components/snackbar-overlay";
import { APP_ROUTES } from "./constants";
import { BACKEND_URL } from "./env";
import { fetchUserProfile } from "./helpers/fetch-profile";
import { isPlatformAdminProfile } from "./helpers/get-user-role";
import { isUnknownPath } from "./helpers/is-unknown-path";
import de from "./locales/de.json";
import en from "./locales/en.json";
import { AdminPlugin, AdminPluginState } from "./pages/admin-plugin";
import { HomePlugin, HomePluginState } from "./pages/home-plugin";
import {
  HomePortalPlugin,
  HomePortalPluginState,
} from "./pages/home-portal-plugin";
import { MagicLinkPlugin } from "./pages/magic-link-plugin";
import { NotFoundPlugin } from "./pages/not-found-plugin";
import { ResetPasswordPlugin } from "./pages/reset-password-plugin";
import { theme } from "./theme";
import { App as AppEntity, Client } from "./types/entities";
import { Profile } from "./types/profile";

export type HomePortalAppState = Omit<
  DefaultOverlayPartialAppState,
  "snackbar"
> &
  Pick<SnackbarOverlayProps, "snackbar"> &
  UniversalAppState &
  HomePluginState &
  HomePortalPluginState &
  AdminPluginState &
  LoginPluginPartialAppState<Profile> & {
    clients: Client[];
    apps: AppEntity[];
    language: SupportedLanguage;
  };

const resetPasswordPlugin = new ResetPasswordPlugin();

const loginPlugin = new LoginPlugin<Profile, HomePortalAppState>(
  APP_ROUTES.home,
  BACKEND_URL,
  "PIMO Product Registration Portal",
  {
    whitelistUrls: [
      APP_ROUTES.home,
      APP_ROUTES.magicLink,
      APP_ROUTES.resetPassword,
      // Logged-out users on an unknown URL get the 404 page instead of the login redirect:
      // route changes are checked against the route pattern, the first load against the URL.
      "*",
      ...(isUnknownPath(window.location.pathname)
        ? [window.location.pathname]
        : []),
    ],
    redirectToBackendLoginIfLoggedOut: true,
    createLoginRoute: false,
    automaticallyDetectEndedSessions: true,
  }
);
const homePlugin = new HomePlugin();
const homePortalPlugin = new HomePortalPlugin();
const adminPlugin = new AdminPlugin();

const app = App.create<HomePortalAppState>(
  composeInitialState<HomePortalAppState>({
    ...initialDefaultOverlayState,
    ...loginPlugin.getInitialState(),
    isLoading: false,
    registration: {
      isSubmitting: false,
      submitStatus: { type: null, message: "" },
      showSuccessDialog: false,
    },
    clients: [],
    apps: [],
    isOpen: false,
    language: L10nPlugin.detectBrowserLanguage({
      shouldDetectBrowserLanguage: true,
      supportedLanguages: ["en", "de"],
      defaultLanguage: "de",
    }),
  }),
  APP_KEY
);
const magicLinkPlugin = new MagicLinkPlugin(() => loginPlugin.refreshSession());
app.setTheme(theme);
//
// Register enhanced L10nPlugin with locales and language detection
const l10nPlugin = new L10nPlugin<HomePortalAppState>({
  supportedLanguages: ["en", "de"],
  defaultLanguage: "de",
  detectBrowserLanguage: true,
  storageKey: "language",
  locales: {
    en,
    de,
  },
});
app.registerPlugin(l10nPlugin);

const overlayView = app.createOverlayView({ name: "Overlay" });

const overlay = overlayView.addComponent({
  component: SnackbarOverlay,
});

overlay.mapState((state) => ({
  snackbar: state.snackbar,
}));

overlay.on("overlay:close-snackbar", () => {
  app.patchAppState({
    snackbar: undefined,
  });
});

loginPlugin.on("login-status-changed", async (evt) => {
  if (!evt.payload?.isLoggedIn) {
    return;
  }

  const profile = await fetchUserProfile();
  if (profile) {
    // Role is resolved by HomePortalPlugin when the selected client is known;
    // resolving here would mislabel the Owner because we don't have the client.
    app.patchAppState({
      isOpen: false,
      userProfile: { ...profile },
    });
  } else {
    app.patchAppState({
      isOpen: false,
    });
  }
});

app.registerPlugin(loginPlugin);
app.registerPlugin(homePlugin);
app.registerPlugin(homePortalPlugin);
app.registerPlugin(adminPlugin);
app.registerPlugin(resetPasswordPlugin);
app.registerPlugin(magicLinkPlugin);
app.registerPlugin(new NotFoundPlugin());

magicLinkPlugin.route?.on("load", async () => {
  await magicLinkPlugin.redeemCurrentLink();
});

// Redirect logged-in users from "/": platform admins to "/admin", everyone
// else to "/home-portal".
homePlugin.route?.on("load", async () => {
  const isLoggedIn = await loginPlugin.isLoggedIn();
  if (!isLoggedIn) return;

  let profile = app.getAppState().userProfile;
  if (!profile) {
    profile = (await fetchUserProfile()) ?? (await fetchUserProfile());
    if (profile) {
      app.patchAppState({ userProfile: { ...profile } });
    }
  }

  if (isPlatformAdminProfile(profile)) {
    app.navigate(APP_ROUTES.admin);
  } else {
    app.navigate(APP_ROUTES.homePortal);
  }
});

const RenderedApp = app.render();

export default RenderedApp;
